import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao, SearchProduto } from '../../Components';
import { api, getListAllAPI, getUserId } from '../../services';
import { STATUS_ATIVO, formatDateDDMMYYYY } from '../../utils';

const styles = {
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: 'black',
  },
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography style={styles.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          style={styles.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const NOMES_ANDAR = ['Térreo', '1º Aéreo', '2º Aéreo'];

function getNomeAndar(andar) {
  return NOMES_ANDAR[andar - 1] || `Andar ${andar}`;
}

const DialogoFazerSaida = forwardRef(({ handleClose = () => {} }, ref) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [produto, setProduto] = useState(null);
  const [combo, setCombo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [buscou, setBuscou] = useState(false);

  const [outrosEnderecos, setOutrosEnderecos] = useState(null);
  const [carregandoOutros, setCarregandoOutros] = useState(false);

  const refDialogoConfirmacao = useRef(null);
  const refSearch = useRef(null);

  const handleCloseDialog = () => {
    setOpen(false);
    setSearch('');
    setProduto(null);
    setCombo(null);
    setBuscou(false);
    setOutrosEnderecos(null);
  };

  useImperativeHandle(ref, () => ({
    handleOpen() {
      setOpen(true);
    },
  }));

  // O SearchProduto já decide sozinho se o que foi digitado/bipado é um
  // código de barras (começa com número, resolve id=0) ou um produto
  // escolhido no autocomplete por nome (já vem com o id).
  async function handleSearchProduto(codigo, id) {
    setCarregando(true);
    setBuscou(false);
    setCombo(null);
    setOutrosEnderecos(null);

    const produtoData = await getListAllAPI(
      'produtos',
      ['id', 'asc'],
      id ? { id } : { codigo, status: STATUS_ATIVO },
      [],
    );

    if (produtoData.data.length === 0) {
      enqueueSnackbar('Produto não encontrado!', { variant: 'error' });
      setProduto(null);
      setCarregando(false);
      if (refSearch.current) refSearch.current.focus();
      return;
    }

    const produtoEncontrado = produtoData.data[0];
    setProduto(produtoEncontrado);

    // Já vem ordenado por validade mais próxima e, em caso de empate, por
    // maior tempo de permanência — só pego o primeiro, sem mostrar lista.
    // Saída pelo empilhador: 1º/2º Aéreo ou o térreo quando NÃO é exposição
    // em loja (o backend já exclui o térreo-exposição, que é gerido só pela
    // tela "Exposição em Loja").
    const response = await api.get(
      `/enderecos_exposicao_andares_produtos/combos/${produtoEncontrado.id}`,
    );
    setCombo(response.data.length > 0 ? response.data[0] : null);
    setBuscou(true);
    setCarregando(false);
  }

  // Só visualização — mostra todo endereço que tem esse produto (inclusive
  // térreo de exposição em loja, que o endpoint de combos exclui de
  // propósito por não ser opção de saída manual), menos o que já está
  // sugerido acima pra sair.
  async function handleMostrarOutrosEnderecos() {
    setCarregandoOutros(true);
    try {
      const response = await api.get(
        `/enderecos_exposicao_andares_produtos/enderecos_do_produto/${produto.id}`,
      );
      const outros = response.data.filter(
        (e) => !(e.andar === combo.andar && e.codigo === combo.codigo),
      );
      setOutrosEnderecos(outros);
    } finally {
      setCarregandoOutros(false);
    }
  }

  function handleConfirmarSaida() {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'REGISTRAR SAÍDA',
        `Confirma a saída de "${produto.nome}" do endereço ${
          combo.codigo
        }, ${getNomeAndar(combo.andar)}?`,
        combo,
      );
    }
  }

  async function handleConfirma(row) {
    try {
      await api.put(
        `/enderecos_exposicao_andares_produtos/status/inativar/${row.andar_produto_id}`,
        { user_id: getUserId() },
      );
      enqueueSnackbar('Saída registrada com sucesso!', { variant: 'success' });
      handleCloseDialog();
      handleClose();
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" onClose={handleCloseDialog}>
          Fazer Saída
        </DialogTitle>
        <MuiDialogContent>
          <SearchProduto
            ref={refSearch}
            label="Nome ou código de barras do produto"
            value={search}
            onChange={setSearch}
            searchHandle={handleSearchProduto}
            filters={{}}
          />

          {carregando ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px',
              }}
            >
              <CircularProgress size={24} />
            </div>
          ) : null}

          {!carregando && buscou ? (
            combo ? (
              <>
                <Paper
                  elevation={2}
                  style={{ padding: '16px', marginTop: '16px' }}
                >
                  <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                    {produto.nome}
                  </Typography>
                  <Typography variant="body1">
                    Endereço: <strong>{combo.codigo}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Andar: <strong>{getNomeAndar(combo.andar)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Validade mais próxima:{' '}
                    <strong>
                      {combo.proxima_validade
                        ? formatDateDDMMYYYY(combo.proxima_validade)
                        : 'Sem validade'}
                    </strong>
                  </Typography>
                  <Typography variant="body1">
                    Permanência:{' '}
                    <strong>
                      {combo.dias_permanencia !== null
                        ? `${combo.dias_permanencia} dia${
                            combo.dias_permanencia === 1 ? '' : 's'
                          }`
                        : '-'}
                    </strong>
                  </Typography>
                </Paper>

                {outrosEnderecos === null ? (
                  <Button
                    onClick={handleMostrarOutrosEnderecos}
                    disabled={carregandoOutros}
                    style={{ marginTop: '8px' }}
                  >
                    {carregandoOutros
                      ? 'Carregando...'
                      : 'Mostrar outros endereços'}
                  </Button>
                ) : (
                  <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: '16px', marginTop: '8px' }}
                  >
                    <Typography
                      variant="subtitle2"
                      style={{ fontWeight: 'bold' }}
                      gutterBottom
                    >
                      Outros endereços com esse produto (só visualização)
                    </Typography>
                    {outrosEnderecos.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        Não há mais nenhum endereço com esse produto.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Endereço</TableCell>
                              <TableCell>Andar</TableCell>
                              <TableCell>Validade</TableCell>
                              <TableCell>Permanência</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {outrosEnderecos.map((endereco) => (
                              <TableRow key={`${endereco.andar}-${endereco.codigo}`}>
                                <TableCell>
                                  <strong>{endereco.codigo}</strong>
                                </TableCell>
                                <TableCell>
                                  {getNomeAndar(endereco.andar)}
                                  {endereco.andar === 1 && endereco.terreo_exposicao
                                    ? ' (Exposição em loja)'
                                    : ''}
                                </TableCell>
                                <TableCell>
                                  {endereco.proxima_validade
                                    ? formatDateDDMMYYYY(endereco.proxima_validade)
                                    : 'Sem validade'}
                                </TableCell>
                                <TableCell>
                                  {endereco.dias_permanencia !== null
                                    ? `${endereco.dias_permanencia} dia${
                                        endereco.dias_permanencia === 1 ? '' : 's'
                                      }`
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '16px' }}
              >
                Este produto não está presente em nenhum endereço.
              </Typography>
            )
          ) : null}
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!combo}
            onClick={handleConfirmarSaida}
          >
            Confirmar Saída
          </Button>
        </MuiDialogActions>
      </Dialog>
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
    </>
  );
});

export default DialogoFazerSaida;
